import type React from 'react'
import type { LoginBanner, LoginBannerSettings } from '@/src/types/store'

export type { LoginBanner, LoginBannerSettings }

export const DEFAULT_SETTINGS: LoginBannerSettings = {
  position_x: 0.5,
  position_y: 0.5,
  zoom: 1,
  overlay: true,
  overlay_strength: 'medium'
}

// LoginImage.png is 756 x 982. The banner is always drawn at full height with this
// width-to-height ratio, so the crop frame uses the same shape at any size.
export const FRAME = { width: 756, height: 982 }

export const OVERLAY_ALPHA: Record<LoginBannerSettings['overlay_strength'], number> = {
  light: 0.45,
  medium: 0.7,
  strong: 0.88
}

// The curved left edge of the default PNG, as a mask so a custom image gets the
// same cut.
const WAVE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 756 982" preserveAspectRatio="none">' +
  '<path d="M22 0 C80 120 100 260 72 380 C40 520 -10 640 10 760 C25 860 60 940 104 982 H756 V0 Z" fill="#000"/>' +
  '</svg>'
const WAVE_MASK = `url("data:image/svg+xml,${encodeURIComponent(WAVE_SVG)}")`

export const maskStyle: React.CSSProperties = {
  WebkitMaskImage: WAVE_MASK,
  maskImage: WAVE_MASK,
  WebkitMaskSize: '100% 100%',
  maskSize: '100% 100%',
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat'
}

export const settingsOf = (banner: LoginBanner): LoginBannerSettings => ({
  position_x: banner.position_x,
  position_y: banner.position_y,
  zoom: banner.zoom,
  overlay: banner.overlay,
  overlay_strength: banner.overlay_strength
})

// The image box is `zoom` times the frame, cover-fitted, and offset so the focal
// point (position_x, position_y) of the picture sits on the same fraction of the
// frame. zoom 1 at (0.5, 0.5) is a plain centred cover. `maxWidth: none` undoes
// Tailwind's preflight `img { max-width: 100% }`.
export const imageStyle = ({
  position_x,
  position_y,
  zoom
}: LoginBannerSettings): React.CSSProperties => ({
  position: 'absolute',
  width: `${zoom * 100}%`,
  height: `${zoom * 100}%`,
  maxWidth: 'none',
  left: `${position_x * (1 - zoom) * 100}%`,
  top: `${position_y * (1 - zoom) * 100}%`,
  objectFit: 'cover',
  objectPosition: `${position_x * 100}% ${position_y * 100}%`
})

export const overlayStyle = (
  settings: LoginBannerSettings,
  primary: string,
  secondary: string
): React.CSSProperties => ({
  position: 'absolute',
  inset: 0,
  background: `linear-gradient(135deg, ${secondary} 0%, ${primary} 100%)`,
  opacity: settings.overlay ? OVERLAY_ALPHA[settings.overlay_strength] : 0,
  pointerEvents: 'none'
})

// How far the picture travels, in frame pixels, as a position goes 0 -> 1 on each
// axis. Used by the editor to turn a pointer drag into a position delta. Zero on an
// axis means the picture cannot move that way at this zoom.
export const dragSlack = (
  frame: { width: number; height: number },
  natural: { width: number; height: number } | null,
  zoom: number
) => {
  const frameAspect = FRAME.width / FRAME.height
  const imageAspect = natural ? natural.width / natural.height : frameAspect
  return {
    x: frame.width * (zoom * Math.max(1, imageAspect / frameAspect) - 1),
    y: frame.height * (zoom * Math.max(1, frameAspect / imageAspect) - 1)
  }
}

// Source pixels shown across the frame width. Under 756 the banner looks soft on a
// large screen; the editor warns but still allows the save.
export const visibleSourceWidth = (natural: { width: number; height: number }, zoom: number) => {
  const coverScale = Math.max(FRAME.width / natural.width, FRAME.height / natural.height)
  return FRAME.width / (coverScale * zoom)
}
